import {
    type AnchorHTMLAttributes,
    type MouseEvent,
    type ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    useMemo,
    useRef
} from "react";
import {
    type DocsLanguage,
    useDocsI18n
} from "./i18n";
import {
    NCoreUIKitTheme
} from "ncore-ui-kit";

type DocsRouterContextType = {
    getHref: (href: string) => string;
    navigate: (href: string) => void;
    path: string;
};

const DOCS_LANGUAGES: Array<DocsLanguage> = [
    "tr",
    "en"
];

const DocsRouterContext = createContext<DocsRouterContextType>({
    navigate: () => undefined,
    getHref: (href) => href,
    path: "/"
});

export const parseDocsPathname = (pathname: string): {
    language: DocsLanguage | null;
    path: string;
} => {
    const [
        ,
        firstSegment = "",
        ...restSegments
    ] = pathname.split("/");

    const language = DOCS_LANGUAGES.find((item) => item === firstSegment);

    if(!language) {
        return {
            path: pathname || "/",
            language: null
        };
    }

    return {
        path: restSegments.length ? `/${restSegments.join("/")}` : "/",
        language
    };
};

export const getDocsHref = (href: string, language: DocsLanguage) => {
    if(!href.startsWith("/") || href.startsWith("//") || parseDocsPathname(href.split(/[?#]/)[0] ?? "").language) {
        return href;
    }

    return href === "/" ? `/${language}` : `/${language}${href.startsWith("/?") || href.startsWith("/#") ? href.slice(1) : href}`;
};

const normalizePath = (pathname: string) => {
    if(pathname.length > 1 && pathname.endsWith("/")) {
        return pathname.slice(0, -1);
    }

    return pathname;
};

const scrollToHash = (hash: string, offset: number) => {
    if(!hash) {
        window.scrollTo({
            top: 0
        });

        return;
    }

    const target = document.getElementById(decodeURIComponent(hash.slice(1)));

    if(target) {
        const header = document.querySelector("header");

        const headerHeight = header ? header.getBoundingClientRect().height : 0;

        window.scrollTo({
            top: window.scrollY + target.getBoundingClientRect().top - headerHeight - offset
        });
    }
};

export const DocsRouterProvider = ({
    children
}: {
    children: ReactNode;
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        setLanguage,
        language
    } = useDocsI18n();

    const [
        path,
        setPath
    ] = useState(() => normalizePath(parseDocsPathname(window.location.pathname).path));

    const languageRef = useRef(language);

    languageRef.current = language;

    useEffect(() => {
        const current = parseDocsPathname(window.location.pathname);

        if(current.language === language) {
            return;
        }

        window.history.replaceState(window.history.state, "", `${getDocsHref(normalizePath(current.path), language)}${window.location.search}${window.location.hash}`);
    }, [
        language
    ]);

    const scrollOffsetRef = useRef(spaces.spacingLg);

    scrollOffsetRef.current = spaces.spacingLg;

    useEffect(() => {
        const handlePopState = () => {
            const current = parseDocsPathname(window.location.pathname);

            if(current.language && current.language !== languageRef.current) {
                setLanguage(current.language);
            }

            setPath(normalizePath(current.path));
        };

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, []);

    useEffect(() => {
        if(window.location.hash) {
            requestAnimationFrame(() => scrollToHash(window.location.hash, scrollOffsetRef.current));
        }
    }, []);

    useEffect(() => {
        const handleDocumentClick = (event: globalThis.MouseEvent) => {
            if(event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }

            const anchor = event.target instanceof Element ? event.target.closest("a[href]") : null;

            if(!(anchor instanceof HTMLAnchorElement) || anchor.target === "_blank" || anchor.hasAttribute("download") || anchor.getAttribute("aria-disabled") === "true") {
                return;
            }

            const url = new URL(anchor.href, window.location.href);

            if(url.origin !== window.location.origin) {
                return;
            }

            event.preventDefault();

            navigateRef.current(`${url.pathname}${url.hash}`);
        };

        document.addEventListener("click", handleDocumentClick, true);

        return () => {
            document.removeEventListener("click", handleDocumentClick, true);
        };
    }, []);

    const navigate = useCallback((href: string) => {
        const url = new URL(href, window.location.origin);

        const target = parseDocsPathname(url.pathname);

        const nextPath = normalizePath(target.path);

        const nextLanguage = target.language ?? languageRef.current;

        const isSamePath = nextPath === normalizePath(parseDocsPathname(window.location.pathname).path) && nextLanguage === languageRef.current;

        const search = isSamePath ? window.location.search : url.search;

        const nextUrl = `${getDocsHref(nextPath, nextLanguage)}${search}${url.hash}`;

        if(nextLanguage !== languageRef.current) {
            setLanguage(nextLanguage);
        }

        if(isSamePath && url.hash) {
            window.history.pushState(null, "", nextUrl);

            scrollToHash(url.hash, scrollOffsetRef.current);

            return;
        }

        window.history.pushState(null, "", nextUrl);

        setPath(nextPath);

        requestAnimationFrame(() => scrollToHash(url.hash, scrollOffsetRef.current));
    }, [
        setLanguage
    ]);

    const getHref = useCallback((href: string) => getDocsHref(href, language), [
        language
    ]);

    const navigateRef = useRef(navigate);

    navigateRef.current = navigate;

    const value = useMemo(() => ({
        navigate,
        getHref,
        path
    }), [
        navigate,
        getHref,
        path
    ]);

    return <DocsRouterContext.Provider
        value={value}
    >
        {children}
    </DocsRouterContext.Provider>;
};

export const useDocsRouter = () => useContext(DocsRouterContext);

export const DocsLink = ({
    children,
    onClick,
    href,
    ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
    children: ReactNode;
    href: string;
}) => {
    const {
        navigate,
        getHref
    } = useDocsRouter();

    const isExternal = /^https?:\/\//.test(href);

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);

        if(isExternal || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }

        event.preventDefault();

        navigate(href);
    };

    return <a
        {...props}
        rel={isExternal ? "noreferrer" : props.rel}
        href={isExternal ? href : getHref(href)}
        onClick={handleClick}
    >
        {children}
    </a>;
};
